// @ts-strict-ignore
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import { useEffect } from 'react';
import { createGlobalStyle } from 'styled-components';

import { initializeWebVitals } from '../src/analytics/eventableWebVitals';
import AppProviders from '../src/AppProviders';
import { SetFAQStructuredData } from '../src/components/Marketing/FAQ/StructuredFAQ';
import * as fonts from '../src/index.css';

// Global reset, in a low-priority `reset` cascade layer, and the app-wide
// cascade-layer order declaration. Must load before the DS styles below so
// the layer order is established early. See STCAQ-7178.
import '../src/styles/reset.css';

// CarvanaDS Styles (wrapped into the `ds` cascade layer at build time by
// webpack/wrap-css-layer.js so embedded MFE styling wins over them).
import '@carvana/ds-styles/cvna-theme.css';
import '@carvana/ds-styles/cvna-typography.css';
import '@carvana/ds-styles/fonts.css';
import '@carvana/ds-styles/reference.css';
import '@carvana/ds-styles/typography.css';
import '@carvana/ds-button/styles.css';
import '@carvana/ds-link-button/styles.css';
import '@carvana/ds-modal/styles.css';
import '@carvana/ds-select/styles.css';
import '@carvana/ds-tabs/styles.css';
import '@carvana/ds-text-input/styles.css';
import '@carvana/ds-popup/styles.css';

const GlobalStyle = createGlobalStyle`
    ${fonts}

    body {
      background: #e4ecf0;
      overflow: unset;
    }
`;

const gtm = process.env.CARVANA_APP_GTM_ID;

function MyApp({
  Component,
  pageProps,
  cookies
}: AppProps & {
  cookies: string;
  userAgent: string;
}) {
  useEffect(() => {
    initializeWebVitals();
  }, []);

  const metaDescription =
    pageProps.vehicleInfo?.vehicleDisplayName && pageProps.vehicleInfo.vehicleDisplayName !== 'Car'
      ? `Track your ${pageProps.vehicleInfo.vehicleDisplayName}'s value with Carvana Value Tracker and get automatic, personal updates on your car's worth. Share your vehicle details to start now!`
      : "Track your car's value with Carvana Value Tracker and get automatic, personal updates on your car's worth. Share your vehicle details to start now!";

  const metaTitle =
    pageProps.vehicleInfo?.vehicleDisplayName && pageProps.vehicleInfo.vehicleDisplayName !== 'Car'
      ? `Carvana | Track Your ${pageProps.vehicleInfo.vehicleDisplayName}'s Value Online`
      : "Carvana | Track Your Car's Value Online";

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=5,shrink-to-fit=no"
        />
        <meta
          name="description"
          data-cv-test="valueTrackerSeoDescription"
          content={metaDescription}
        />
        <meta name="theme-color" content="#00619e" />
        <title>{metaTitle}</title>
        <link rel="canonical" href="https://www.carvana.com/value-tracker" />
        <link
          rel="manifest"
          href={`${process.env.CARVANA_APP_STC_SHELL_PUBLIC_URL}/manifest.json`}
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={`${process.env.CARVANA_APP_CDN_URL}/icons/apple/apple-touch-icon.png`}
          crossOrigin=""
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`${process.env.CARVANA_APP_CDN_URL}/icons/favicon-32x32.png`}
          crossOrigin=""
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`${process.env.CARVANA_APP_CDN_URL}/icons/favicon-16x16.png`}
          crossOrigin=""
        />
        <link
          rel="icon"
          type="image/x-icon"
          href={`${process.env.CARVANA_APP_CDN_URL}/icons/favicon.ico`}
          crossOrigin=""
        />
        <link
          rel="preload"
          href="https://assets.fastly.carvana.io/fonts/BrandonText-Light.woff2"
          crossOrigin=""
          as="font"
          type="font/woff2"
        />
        <link
          rel="preload"
          href="https://assets.fastly.carvana.io/fonts/BrandonText-Bold.woff2"
          crossOrigin=""
          as="font"
          type="font/woff2"
        />
        <link
          rel="preload"
          href="https://assets.fastly.carvana.io/fonts/BrandonText-Regular.woff2"
          crossOrigin=""
          as="font"
          type="font/woff2"
        />
      </Head>
      <Script
        id="structure-data"
        key="structured-data"
        type="application/ld+json"
        // ref from https://nextjs.org/learn/seo/rendering-and-ranking/metadata - Structured Data and Json-LD section
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={SetFAQStructuredData()}
      />
      <Script
        id="gtm-script"
        strategy="afterInteractive"
        // eslint-disable-next-line react/no-danger -- entities will be created unless dangerously set
        dangerouslySetInnerHTML={{
          __html: `
          (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js'
            });
            var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src =
                'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${gtm}');
          `
        }}
      />
      <GlobalStyle />
      <AppProviders pageProps={pageProps} cookies={cookies}>
        <Component {...pageProps} />
      </AppProviders>
    </>
  );
}

MyApp.getInitialProps = async (
  context: AppContext
): Promise<
  AppInitialProps & {
    cookies: string;
  }
> => {
  const ctx = await App.getInitialProps(context);
  const cookies = context.ctx.req?.headers?.cookie;

  return {
    ...ctx,
    cookies
  };
};

export default MyApp;
